import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'
import DataTable from '../components/shared/DataTable'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import { useData, transformPortfolioCompanies } from '../hooks/useApi'

/**
 * Portfolio Page - displays all portfolio companies and their performance
 * Shows companies the user is invested in with search, filtering, and detailed information
 * Uses standardized data fetching patterns with centralized error handling
 */
const PortfolioPage = () => {
  const navigate = useNavigate()
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('')

  // Single hook with parallel fetching - no initial filters
  const { data, loading, error, refetch, refetchWithFilters } = useData('portfolio')
  
  // Destructure the parallel-fetched data
  const { companies } = data || {}

  // Transform companies data using centralized transformer
  const portfolioCompanies = transformPortfolioCompanies(companies)

  // Apply server-side filtering when search term changes
  useEffect(() => {
    // Only make API calls when user actually changes the search term
    // Don't make calls for empty search on initial renders
    if (searchTerm.trim()) {
      // User is searching - make API call with search filter
      const filters = { search: searchTerm.trim() }
      refetchWithFilters(filters)
    }
  }, [searchTerm])

  // Handle company actions
  const handleViewDetails = (company) => {
    // Navigate to company details page
    navigate(`/portfolio/${company.id}`)
  }

  // Handle retry on error - single refetch call
  const handleRetry = () => {
    refetch()
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Portfolio Companies
        </h1>
        <p className="text-gray-600 text-lg">
          View all your portfolio companies
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <Card shadow="medium" padding="large">
          <LoadingSpinner 
            size="large" 
            text="Loading portfolio companies..." 
          />
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card shadow="medium" padding="large">
          <div className="text-center py-8">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <p className="text-gray-600 text-lg mb-4">Failed to load portfolio data</p>
            <p className="text-gray-500 mb-6">Please try again or contact support if the problem persists.</p>
            <Button onClick={handleRetry} variant="primary">
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Main Content - Only show when not loading and no error */}
      {!loading && !error && (
        <>
      {/* Search Section */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Companies Table */}
      <Card 
        shadow="medium"
        padding="none"
        // title="Portfolio Companies"
        // subtitle="Companies in your investment portfolio"
      >
        {/* Desktop: DataTable */}
        <div className="hidden lg:block">
          <DataTable
            columns={[
              { 
                key: 'company', 
                label: 'Company',
                render: (value, row) => (
                  <div className="flex items-center gap-3">
                    {row.logo && (
                      <img 
                        src={row.logo} 
                        alt={`${value} logo`}
                        className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    )}
                    <div>
                      <div className="font-semibold text-gray-900">{value}</div>
                      {row.hqProvince !== 'Unknown' && <div className="text-sm text-gray-500">{row.hqProvince}</div>}
                    </div>
                  </div>
                )
              },
              { key: 'industry', label: 'Industry' },
              { key: 'totalInvested', label: 'Total Invested' },
              { key: 'deals', label: 'Deals' },
              {
                key: 'actions',
                label: '',
                render: (value, row) => (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => handleViewDetails(row)}
                      className="text-sm"
                    >
                      Details →
                    </Button>
                  </div>
                )
              }
            ]}
            data={portfolioCompanies}
            pageSize={10}
            onRowClick={(row) => handleViewDetails(row)}
          />
        </div>

        {/* Mobile & Tablet: Card Layout */}
        <div className="lg:hidden">
          <div className="space-y-4 divide-y divide-gray-200 p-4 pt-0 [&>*:first-child]:pt-0">
            {portfolioCompanies.map((company) => (
              <div key={company.id} className="pt-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {company.logo && (
                        <img 
                          src={company.logo} 
                          alt={`${company.company} logo`}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {company.company}
                        </h3>
                        {company.hqProvince !== 'Unknown' && (
                          <span className="text-sm text-gray-600">{company.hqProvince}</span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {company.industry}
                    </p>
                    <div className="flex items-center text-sm text-gray-600">
                      <span>Total: {company.totalInvested}&nbsp;</span>
                      <span className="italic">&#40;{company.deals} {company.deals === 1 ? 'deal' : 'deals'}&#41;</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => handleViewDetails(company)}
                    className="flex-1 text-sm"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* No results message */}
          {portfolioCompanies.length === 0 && (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">🏢</div>
              <p className="text-gray-600 text-lg mb-2">No companies found</p>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search terms' : 'No portfolio companies available'}
              </p>
            </div>
          )}
        </div>
      </Card>
        </>
      )}
    </div>
  )
}

export default PortfolioPage
